import IPT from 'react-immutable-proptypes';
import PT from 'prop-types';
import { useEffect } from 'react';
import { STATUS } from '../../constants';
import AppContainer from 'edrolo/domains/app/components/AppContainer';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import StaffRibbon from 'edrolo/domains/app/components/StaffRibbon';
import { QuestionSetTopBar } from '../../components/QuestionSetTopBar';
import QuestionSetIndex from '../../components/QuestionSetIndex';
import QuestionSet from '../../components/QuestionSet';
import urlResolver from 'edrolo/utils/URLResolver';
import { SCHOOL_CLASS_TYPE } from 'edrolo/domains/bySchoolClass/constants';
import { useClassDetail } from 'edrolo/domains/bySchoolClass/hooks';
import { Loader } from 'edrolo-ui';
import { FAILURE_REASON } from 'edrolo/domains/net/constants';

const buildDocumentTitle = (exam, question) =>
  `${exam.get('title')} - Q${question.get('full_serial')}`;

const QuestionSetPage = ({
  examId,
  classId,
  questionId,
  sittingId,
  exam,
  question,
  examStatus,
  response,
  loadExam,
  ensureSittingLoaded,
  isTeacher,
  rootQuestionDescendants,
  submitQuestionResponse,
  submitChecklistReflectionItem,
  submitGrok,
  submitMark,
  sections,
  numQuestions,
  questionsBySectionId,
  getQuestionInstructionText,
  questionResponses,
  searchParams,
  filterToggle,
  prevNextIds,
  history, // from routes.js
  examError,
}) => {
  useEffect(() => {
    if (examStatus === STATUS.UNINITIALIZED) {
      loadExam(examId);
    }
  }, [examId, examStatus, loadExam]);

  useEffect(() => {
    ensureSittingLoaded(examId);
  }, [ensureSittingLoaded, examId]);

  const buildQuestionSetUrl = (examId, questionId, classId) => {
    const url = questionId
      ? urlResolver.reverse('question-set-question', {
          examId,
          questionId,
          classId,
        })
      : urlResolver.reverse('question-set-index', {
          examId,
          classId,
        });

    return searchParams ? `${url}?${searchParams.toString()}` : url;
  };

  const { classDetail } = useClassDetail(classId);

  const schoolClassExpirationDate = classDetail.get('expiration_date');
  const classType = classDetail.get('class_type');
  const schoolClassIsTrial =
    classType === SCHOOL_CLASS_TYPE.student_trial ||
    classType === SCHOOL_CLASS_TYPE.teacher_trial;

  const schoolClassCode = classDetail.get('title');

  // If the sitting is not loaded, it will be possible for responses to
  // be created in an invalid state (ie: without a sitting).
  const isLoading = examStatus !== STATUS.READY || !exam || !sittingId;

  const analyticsPayload = {
    class_id: classId,
    exam_id: examId,
  };

  const documentTitle =
    exam && question ? buildDocumentTitle(exam, question) : 'Edrolo';

  const coursePageUrl = urlResolver.reverse('course-page', {
    classId: classId,
  });

  if (
    examStatus === STATUS.FAILURE &&
    examError?.get('failureReason') === FAILURE_REASON.NOT_FOUND_ERROR
  ) {
    return (
      <AppContainer>
        <Loader.RedirectingSpinner
          redirectUrl={coursePageUrl}
          redirectMessage="Redirecting to the course page"
          renderLoadingIndicator={() => {
            return <LoadingIndicator loadingText="Resource not available." />;
          }}
        />
      </AppContainer>
    );
  }

  return (
    <AppContainer
      bodyClassName="lesson-questions"
      documentTitle={documentTitle}
      renderTopBar={() => (
        <QuestionSetTopBar
          isTeacher={isTeacher}
          classId={classId}
          exam={exam}
        />
      )}
      renderLayout={(content) => (
        <AppContainer.BlankLayout>{content}</AppContainer.BlankLayout>
      )}
    >
      {isLoading && <LoadingIndicator loadingText="Loading Question Set" />}

      {!isLoading && question && (
        <QuestionSet
          classId={classId}
          examId={examId}
          questionId={questionId}
          sittingId={sittingId}
          exam={exam}
          question={question}
          response={response}
          isTeacher={isTeacher}
          prevNextIds={prevNextIds}
          rootQuestionDescendants={rootQuestionDescendants}
          submitQuestionResponse={submitQuestionResponse}
          submitChecklistReflectionItem={submitChecklistReflectionItem}
          submitGrok={submitGrok}
          submitMark={submitMark}
          buildQuestionSetUrl={buildQuestionSetUrl}
          searchParams={searchParams}
          push={history.push}
        />
      )}

      {!isLoading && !questionId && (
        <QuestionSetIndex
          examId={examId}
          exam={exam}
          classId={classId}
          sections={sections}
          numQuestions={numQuestions}
          questionsBySectionId={questionsBySectionId}
          isTeacher={isTeacher}
          getQuestionInstructionText={getQuestionInstructionText}
          questionResponses={questionResponses}
          buildQuestionSetUrl={buildQuestionSetUrl}
          schoolClassCode={schoolClassCode}
          schoolClassExpirationDate={schoolClassExpirationDate}
          schoolClassIsTrial={schoolClassIsTrial}
          analyticsPayload={analyticsPayload}
          searchParams={searchParams}
          filterToggle={filterToggle}
        />
      )}

      {questionId ? (
        <StaffRibbon
          classId={classId}
          links={[
            {
              title: 'Edit in CPT Tools',
              url: `/content/admin/exams/edit/${examId}/#question-${questionId}`,
            },
            {
              title: 'Edit in Admin',
              url: `/secret/exams/examquestion/${questionId}/change/`,
            },
          ]}
        />
      ) : (
        <StaffRibbon
          classId={classId}
          links={[
            {
              title: 'Edit in CPT Tools',
              url: `/content/admin/exams/edit/${examId}/`,
            },
            {
              title: 'Edit in Admin',
              url: `/secret/exams/exam/${examId}/change/`,
            },
          ]}
        />
      )}
    </AppContainer>
  );
};

QuestionSetPage.propTypes = {
  exam: IPT.map,
  examId: PT.string,
  classId: PT.oneOfType([PT.string, PT.number]).isRequired,
  numQuestions: PT.number,
  sections: IPT.list,
  questionsBySectionId: IPT.map,
  questionResponses: PT.any,
  isTeacher: PT.bool,
  getQuestionInstructionText: PT.func,
  searchParams: PT.object.isRequired,
  filterToggle: PT.shape({
    hasSpiciness: PT.bool.isRequired,
    hasWorkingPaths: PT.bool.isRequired,
  }).isRequired,
  questionId: PT.string,
  sittingId: PT.number,
  question: PT.object,
  submitQuestionResponse: PT.func.isRequired,
  submitChecklistReflectionItem: PT.func.isRequired,
  submitGrok: PT.func.isRequired,
  submitMark: PT.func.isRequired,
  response: PT.any,
  rootQuestionDescendants: PT.any,
  examStatus: PT.string.isRequired,
  loadExam: PT.func.isRequired,
  ensureSittingLoaded: PT.func.isRequired,
  prevNextIds: PT.shape({
    prevParentId: PT.number,
    nextParentId: PT.number,
    prevId: PT.number,
    nextId: PT.number,
  }).isRequired,
  history: PT.object.isRequired,
  examError: PT.any,
};

export default QuestionSetPage;
